// Popup Rules Engine for determining when, where, and to whom to show popups
import type {
    PopupRulesSettings,
    PopupVisitorInfo,
    PopupTriggerResult,
    PopupStorageData,
} from "@/shared/types/popup-types";
import { pageViewsThisVisit } from "./page-views";

// ============================================
// LOCAL STORAGE MANAGEMENT
// ============================================

const STORAGE_KEY = 'flowapt_popup_data';

export function getPopupStorageData(): PopupStorageData {
    if (typeof window === 'undefined') return {};

    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        return stored ? JSON.parse(stored) : {};
    } catch (error) {
        console.warn('Failed to parse popup storage data:', error);
        return {};
    }
}

export function setPopupStorageData(data: PopupStorageData): void {
    if (typeof window === 'undefined') return;

    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
        console.warn('Failed to save popup storage data:', error);
    }
}

export function updatePopupStorage(popupId: string, updates: Partial<PopupStorageData[string]>): void {
    const data = getPopupStorageData();
    data[popupId] = { ...data[popupId], ...updates };
    setPopupStorageData(data);
}

// ============================================
// VISITOR DETECTION
// ============================================

export function detectDevice(): 'desktop' | 'mobile' {
    if (typeof window === 'undefined') return 'desktop';
    // 768px — the SAME breakpoint the renderer, the per-device images and the
    // edit overlay use. It was 640 here, so 641-767px viewports were targeted
    // as desktop while RENDERING the mobile layout (audit 10 Aug 2026).
    return window.innerWidth < 768 ? 'mobile' : 'desktop';
}

export function getVisitorInfo(popupId: string): PopupVisitorInfo {
    const device = detectDevice();
    const storageData = getPopupStorageData();
    const popupData = storageData[popupId] || { submissionCount: 0 };

    return {
        device,
        hasSeenPopup: !!popupData.lastShown,
        lastSeenAt: popupData.lastShown,
        submissionCount: popupData.submissionCount,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
        referrer: typeof document !== 'undefined' ? document.referrer : '',
        pageViews: pageViewsThisVisit(),
    };
}

// ============================================
// TIME-BASED RULES
// ============================================

function hasSeenInTimeframe(lastSeen: string | undefined, timeframe: 'hour' | 'day' | 'week' | 'month'): boolean {
    if (!lastSeen) return false;

    const lastSeenTime = new Date(lastSeen).getTime();
    const now = Date.now();

    const timeframes = {
        hour: 60 * 60 * 1000,
        day: 24 * 60 * 60 * 1000,
        week: 7 * 24 * 60 * 60 * 1000,
        month: 30 * 24 * 60 * 60 * 1000,
    };

    return (now - lastSeenTime) < timeframes[timeframe];
}

// ============================================
// FREQUENCY RULES
// ============================================

function checkFrequencyRule(
    frequency: PopupRulesSettings['whom_to_show']['frequency'],
    visitor: PopupVisitorInfo
): boolean {
    switch (frequency) {
        case 'everytime':
            return true;
        case 'once_per_hour':
            return !hasSeenInTimeframe(visitor.lastSeenAt, 'hour');
        case 'once_per_day':
            return !hasSeenInTimeframe(visitor.lastSeenAt, 'day');
        case 'once_per_week':
            return !hasSeenInTimeframe(visitor.lastSeenAt, 'week');
        case 'once_per_month':
            return !hasSeenInTimeframe(visitor.lastSeenAt, 'month');
        default:
            return false;
    }
}

// ============================================
// DEVICE RULES
// ============================================

function checkDeviceRule(
    whereToShow: PopupRulesSettings['where_to_show'],
    visitor: PopupVisitorInfo
): boolean {
    if (visitor.device === 'desktop' && !whereToShow.desktop) {
        return false;
    }
    if (visitor.device === 'mobile' && !whereToShow.mobile) {
        return false;
    }
    return true;
}

// ============================================
// LOCATION RULES (SIMPLIFIED - NO GEO-IP FOR NOW)
// ============================================

function checkLocationRule(
    locationRules: PopupRulesSettings['location_rules'],
    visitor: PopupVisitorInfo
): boolean {
    // Country targeting is enforced SERVER-SIDE in api/popup-settings.ts
    // (Vercel's x-vercel-ip-country header — the browser cannot know its own
    // country). A country-blocked visitor gets skipPopup before any settings
    // reach this engine, so the client check is always permissive.
    void locationRules;
    void visitor;
    return true;
}

// ============================================
// PAGE RULES (URL targeting)
// ============================================

/** A pattern matches the current path when it is a substring of
 *  pathname+search, or, with `*` wildcards, when the wildcard pattern
 *  matches the whole pathname. Comparison is case-insensitive. */
export function pageMatchesPattern(path: string, pattern: string): boolean {
    const p = pattern.trim().toLowerCase();
    if (!p) return false;
    const target = path.toLowerCase();
    if (p.includes('*')) {
        const rx = new RegExp(
            '^' + p.split('*').map((s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('.*') + '$'
        );
        return rx.test(target);
    }
    return target.includes(p);
}

function checkPageRule(rules: PopupRulesSettings): boolean {
    const pr = rules.page_rules;
    if (!pr || pr.rule_type === 'all_pages' || !pr.rule_type) return true;
    if (typeof window === 'undefined') return true;
    const patterns = (pr.patterns ?? []).filter((s) => s && s.trim());
    // An include rule with no patterns would show the popup NOWHERE — treat
    // an empty list as "all pages" so a half-configured rule can't hide it.
    if (patterns.length === 0) return true;
    const path = window.location.pathname + window.location.search;
    const matched = patterns.some((pattern) => pageMatchesPattern(path, pattern));
    return pr.rule_type === 'include' ? matched : !matched;
}

// ============================================
// SCROLL DETECTION
// ============================================

export function getScrollPercentage(): number {
    if (typeof window === 'undefined') return 0;

    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;

    return scrollHeight > 0 ? Math.round((scrollTop / scrollHeight) * 100) : 0;
}

// ============================================
// MAIN RULES ENGINE
// ============================================

export function shouldShowPopup(
    rules: PopupRulesSettings,
    visitor: PopupVisitorInfo
): PopupTriggerResult {
    // Check frequency rules
    if (!checkFrequencyRule(rules.whom_to_show.frequency, visitor)) {
        return {
            shouldShow: false,
            reason: `Frequency rule not met: ${rules.whom_to_show.frequency}`
        };
    }

    // Check device rules
    if (!checkDeviceRule(rules.where_to_show, visitor)) {
        return {
            shouldShow: false,
            reason: `Device not allowed: ${visitor.device}`
        };
    }

    // Check location rules
    if (!checkLocationRule(rules.location_rules, visitor)) {
        return {
            shouldShow: false,
            reason: 'Location rule not met'
        };
    }

    // Check page/URL rules
    if (!checkPageRule(rules)) {
        return {
            shouldShow: false,
            reason: 'Page rule not met: this URL is not targeted'
        };
    }

    // "Show from the Nth page they open": hold the trigger on earlier pages of the visit
    const minPages = Math.floor(Number(rules.when_to_show.min_page_views) || 1);
    if (minPages > 1 && (visitor.pageViews ?? 1) < minPages) {
        return {
            shouldShow: false,
            reason: `Page views: page ${visitor.pageViews ?? 1} of the visit, shows from page ${minPages}`
        };
    }

    // If trigger is timer-based, set delay
    if (rules.when_to_show.trigger_type === 'timer') {
        return {
            shouldShow: true,
            delay: rules.when_to_show.delay_seconds,
            reason: 'Timer trigger'
        };
    }

    // If trigger is scroll-based, indicate that scroll listener should be set up
    if (rules.when_to_show.trigger_type === 'scroll') {
        return {
            shouldShow: true,
            delay: 0,
            reason: 'Scroll trigger - listener will be set up'
        };
    }

    // Exit-intent: listener set up on desktop; mobile falls back to a timer
    if (rules.when_to_show.trigger_type === 'exit_intent') {
        return {
            shouldShow: true,
            delay: rules.when_to_show.delay_seconds ?? 0,
            reason: 'Exit-intent trigger - listener will be set up'
        };
    }

    return {
        shouldShow: false,
        reason: 'Unknown trigger type'
    };
}

// ============================================
// POPUP LIFECYCLE TRACKING
// ============================================

export function recordPopupImpression(popupId: string): void {
    const now = new Date().toISOString();
    updatePopupStorage(popupId, {
        lastShown: now,
        hasSeenToday: true,
        hasSeenThisWeek: true,
        hasSeenThisMonth: true,
    });
}

export function recordPopupSubmission(popupId: string): void {
    const data = getPopupStorageData();
    const popupData = data[popupId] || { submissionCount: 0 };

    updatePopupStorage(popupId, {
        submissionCount: popupData.submissionCount + 1,
    });
}

/** Visitor closed the popup without submitting. Persisted so the teaser can
 *  take over on future page loads instead of the popup re-opening. */
export function recordPopupDismissal(popupId: string): void {
    updatePopupStorage(popupId, {
        dismissedAt: new Date().toISOString(),
    });
}

export function getPopupLifecycle(popupId: string): {
    dismissedAt?: string;
    submissionCount: number;
    issuedCode?: string;
    issuedPass?: PopupStorageData[string]["issuedPass"];
    wheelResult?: PopupStorageData[string]["wheelResult"];
    wheelExpiresAt?: string;
} {
    const data = getPopupStorageData()[popupId];
    return {
        dismissedAt: data?.dismissedAt,
        submissionCount: data?.submissionCount ?? 0,
        issuedCode: data?.issuedCode,
        issuedPass: data?.issuedPass,
        wheelResult: data?.wheelResult,
        wheelExpiresAt: data?.wheelExpiresAt,
    };
}

/** Remember this visitor's minted code / wallet pass so the post-signup
 *  reward teaser can bring the success screen back on a later visit. */
export function recordIssuedReward(
    popupId: string,
    updates: {
        issuedCode?: string;
        issuedPass?: PopupStorageData[string]["issuedPass"];
        wheelResult?: PopupStorageData[string]["wheelResult"];
        wheelExpiresAt?: string;
    },
): void {
    updatePopupStorage(popupId, updates);
}

// ============================================
// SCROLL LISTENER SETUP
// ============================================

export function setupScrollTrigger(
    rules: PopupRulesSettings,
    onTrigger: () => void
): (() => void) | null {
    if (typeof window === 'undefined' || rules.when_to_show.trigger_type !== 'scroll') {
        return null;
    }

    let triggered = false;

    // Runtime default: a popup whose scroll_percentage was never stored used
    // to compare against undefined and NEVER fire (audit 10 Aug 2026).
    const rawPct = rules.when_to_show.scroll_percentage;
    const targetPct =
        typeof rawPct === 'number' && Number.isFinite(rawPct) && rawPct > 0
            ? Math.min(100, rawPct)
            : 50;

    const handleScroll = () => {
        if (triggered) return;

        const scrollPercentage = getScrollPercentage();
        if (scrollPercentage >= targetPct) {
            triggered = true;
            onTrigger();
        }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Return cleanup function
    return () => {
        window.removeEventListener('scroll', handleScroll);
    };
}

// ============================================
// TIMER TRIGGER SETUP
// ============================================

export function setupTimerTrigger(
    delaySeconds: number,
    onTrigger: () => void
): (() => void) {
    const timeoutId = setTimeout(onTrigger, delaySeconds * 1000);

    // Return cleanup function
    return () => {
        clearTimeout(timeoutId);
    };
}

// ============================================
// EXIT-INTENT TRIGGER SETUP
// ============================================

/**
 * Fires when the cursor leaves the viewport through the top edge (the classic
 * about-to-close signal). Desktop only — callers should fall back to a timer
 * on mobile (no cursor to track). Fires at most once.
 */
export function setupExitIntentTrigger(
    onTrigger: () => void
): (() => void) | null {
    if (typeof document === 'undefined') {
        return null;
    }

    let triggered = false;

    const handleMouseOut = (event: MouseEvent) => {
        if (triggered) return;
        // Only when leaving the document itself (not moving between elements)
        // through the top of the viewport.
        if (event.relatedTarget === null && event.clientY <= 0) {
            triggered = true;
            onTrigger();
        }
    };

    document.addEventListener('mouseout', handleMouseOut);

    return () => {
        document.removeEventListener('mouseout', handleMouseOut);
    };
}
